import React from 'react';

export const createPrincipalContext = <Principal extends object>(
  getPrincipal: () => Principal | null
) => {
  type PrincipalProviderState = {
    principal: Principal | null;
    updatePrincipal: () => void;
  };

  const PrincipalContext = React.createContext<PrincipalProviderState | undefined>(undefined);
  PrincipalContext.displayName = 'Principal';

  const PrincipalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [principal, setPrincipal] = React.useState<Principal | null>(getPrincipal());

    const updatePrincipal = () => {
      setPrincipal(getPrincipal());
    };

    window.addEventListener(PRINCIPAL_UPDATED_EVENT, updatePrincipal);

    return (
      <PrincipalContext.Provider value={{ principal, updatePrincipal }}>
        {children}
      </PrincipalContext.Provider>
    );
  };

  const usePrincipal = () => {
    const principal = React.useContext(PrincipalContext);
    if (typeof principal === 'undefined') {
      throw new Error('usePrincipal must be used in the context of a <PrincipalProvider>');
    }
    return principal.principal;
  };

  const useUpdatePrincipal = () => {
    const principal = React.useContext(PrincipalContext);
    if (typeof principal === 'undefined') {
      throw new Error('usePrincipal must be used in the context of a <PrincipalProvider>');
    }
    return principal.updatePrincipal;
  };

  return {
    PrincipalProvider,
    usePrincipal,
    useUpdatePrincipal
  };
};

export const PRINCIPAL_UPDATED_EVENT = 'wild-principalUpdated';
export const refreshPrincipalOutsideReact = () =>
  window.dispatchEvent(new Event(PRINCIPAL_UPDATED_EVENT));
